// packages/isomorphic/src/lib/auth-claims.ts
var MUST_CHANGE_PASSWORD_CLAIM = "mustChangePassword";

// packages/isomorphic/src/lib/calculate-total.ts
import { differenceInDays } from "date-fns";

// packages/isomorphic/src/lib/schedule-time.ts
var SCHEDULE_TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
var MINUTES_PER_DAY = 24 * 60;
function parseScheduleTime(value) {
  const match = SCHEDULE_TIME_PATTERN.exec(value);
  if (!match) {
    throw new Error(`Invalid schedule time: ${value}`);
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  return {
    hours,
    minutes,
    seconds,
    totalMinutes: hours * 60 + minutes + seconds / 60
  };
}
function toScheduleTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid schedule time date: ${value}`);
  }
  return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
}
function calculateDailyWorkingHours(from, to) {
  const fromMinutes = parseScheduleTime(from).totalMinutes;
  let toMinutes = parseScheduleTime(to).totalMinutes;
  if (toMinutes <= fromMinutes) {
    toMinutes += MINUTES_PER_DAY;
  }
  return (toMinutes - fromMinutes) / 60;
}

// packages/isomorphic/src/lib/calculate-total.ts
function calculateTotal(params) {
  const workingDays = differenceInDays(
    new Date(params.scheduleEndDate),
    new Date(params.scheduleStartDate)
  ) + 1;
  const workingHoursPerDay = calculateDailyWorkingHours(
    params.scheduleHoursFrom,
    params.scheduleHoursTo
  );
  const subtotal = params.priceItems.reduce(
    (sum, item) => sum + item.unitPrice * item.count,
    0
  );
  const total = subtotal * workingDays * workingHoursPerDay;
  return { subtotal, workingDays, workingHoursPerDay, total };
}

// packages/isomorphic/src/lib/calculate-monthly-installments.ts
import {
  addMonths,
  differenceInDays as differenceInDays2,
  differenceInMonths,
  getDaysInMonth
} from "date-fns";
var SHORT_TERM_MAX_DAYS = 30;
var SHORT_TERM_SPLIT_OPTIONS = [
  [40, 60],
  [50, 50],
  [60, 40]
];
var MIN_FIRST_PAYMENT_PERCENT = 20;
function isShortTermProject(startDate, endDate) {
  return differenceInDays2(new Date(endDate), new Date(startDate)) <= SHORT_TERM_MAX_DAYS;
}
function calculateMonthlyInstallments(startDate, endDate, firstPaymentPercent) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalMonths = differenceInMonths(end, start);
  if (totalMonths < 1) {
    return [];
  }
  if (totalMonths === 1) {
    return [
      { percentage: firstPaymentPercent, name: null },
      { percentage: 100 - firstPaymentPercent, name: null }
    ];
  }
  const remaining = 100 - firstPaymentPercent;
  const remainingMonths = totalMonths - 1;
  const fullMonthEnd = addMonths(start, totalMonths);
  const partialDays = differenceInDays2(end, fullMonthEnd);
  if (partialDays > 0) {
    const daysInPartialMonth = getDaysInMonth(fullMonthEnd);
    const equalShare2 = Math.round(remaining / remainingMonths * 100) / 100;
    const proratedShare = Math.round(equalShare2 * (partialDays / daysInPartialMonth) * 100) / 100;
    const fullSharesTotal = equalShare2 * (remainingMonths - 1) + proratedShare;
    const lastFullShare = Math.round((remaining - fullSharesTotal) * 100) / 100;
    const installments2 = [
      { percentage: firstPaymentPercent, name: null }
    ];
    for (let i = 0; i < remainingMonths - 1; i++) {
      installments2.push({ percentage: equalShare2, name: null });
    }
    installments2.push({ percentage: lastFullShare, name: null });
    installments2.push({ percentage: proratedShare, name: null });
    return installments2;
  }
  const equalShare = Math.round(remaining / remainingMonths * 100) / 100;
  const othersTotal = equalShare * (remainingMonths - 1);
  const lastShare = Math.round((remaining - othersTotal) * 100) / 100;
  const installments = [
    { percentage: firstPaymentPercent, name: null }
  ];
  for (let i = 0; i < remainingMonths - 1; i++) {
    installments.push({ percentage: equalShare, name: null });
  }
  installments.push({ percentage: lastShare, name: null });
  return installments;
}

// packages/isomorphic/src/lib/offer-timeline.ts
import { addDays } from "date-fns";
function deriveDocumentStatus(timeline, documentId) {
  const documentEvents = timeline.filter((e) => {
    if (e.type !== "DOCUMENT_SUBMITTED" && e.type !== "DOCUMENT_APPROVED" && e.type !== "DOCUMENT_REJECTED") {
      return false;
    }
    const context = e.context;
    return context?.documentId === documentId;
  }).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  if (documentEvents.length === 0) {
    return "NOT_SUBMITTED";
  }
  const latestEvent = documentEvents[0];
  switch (latestEvent.type) {
    case "DOCUMENT_SUBMITTED":
      return "PENDING";
    case "DOCUMENT_APPROVED":
      return "APPROVED";
    case "DOCUMENT_REJECTED":
      return "REJECTED";
    default:
      return "NOT_SUBMITTED";
  }
}
function getDocumentRejectionReason(timeline, documentId) {
  const documentEvents = timeline.filter((e) => {
    if (e.type !== "DOCUMENT_SUBMITTED" && e.type !== "DOCUMENT_APPROVED" && e.type !== "DOCUMENT_REJECTED") {
      return false;
    }
    const context = e.context;
    return context?.documentId === documentId;
  }).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  if (documentEvents.length === 0) {
    return null;
  }
  const latestEvent = documentEvents[0];
  if (latestEvent.type === "DOCUMENT_REJECTED") {
    const context = latestEvent.context;
    return context.reason ?? null;
  }
  return null;
}
function deriveTTStatus(timeline) {
  const ttEvents = timeline.filter(
    (e) => e.type === "TT_UPLOADED" || e.type === "TT_APPROVED" || e.type === "TT_REJECTED"
  ).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  if (ttEvents.length === 0) {
    return "NOT_UPLOADED";
  }
  const latestEvent = ttEvents[0];
  switch (latestEvent.type) {
    case "TT_UPLOADED":
      return "PENDING";
    case "TT_APPROVED":
      return "APPROVED";
    case "TT_REJECTED":
      return "REJECTED";
    default:
      return "NOT_UPLOADED";
  }
}
function getTTRejectionReason(timeline) {
  const ttEvents = timeline.filter(
    (e) => e.type === "TT_UPLOADED" || e.type === "TT_APPROVED" || e.type === "TT_REJECTED"
  ).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  if (ttEvents.length === 0) {
    return null;
  }
  const latestEvent = ttEvents[0];
  if (latestEvent.type === "TT_REJECTED") {
    const context = latestEvent.context;
    return context.reason ?? null;
  }
  return null;
}
function getTTUploadedAt(timeline) {
  const uploadEvent = timeline.filter((e) => e.type === "TT_UPLOADED").sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )[0];
  if (!uploadEvent) {
    return null;
  }
  return new Date(uploadEvent.createdAt);
}
function deriveContractSignatureStatus(timeline) {
  const signatures = timeline.filter((e) => e.type === "CONTRACT_SIGNED");
  const vendorSigned = signatures.some((s) => s.source === "VENDOR");
  const customerSigned = signatures.some((s) => s.source === "CUSTOMER");
  return {
    vendorSigned,
    customerSigned,
    bothSigned: vendorSigned && customerSigned
  };
}
function deriveTTDeadline(timeline) {
  const signatures = timeline.filter((e) => e.type === "CONTRACT_SIGNED");
  const vendorSignature = signatures.find((s) => s.source === "VENDOR");
  const customerSignature = signatures.find((s) => s.source === "CUSTOMER");
  if (!vendorSignature || !customerSignature) {
    return null;
  }
  const lastSignatureDate = new Date(
    Math.max(
      new Date(vendorSignature.createdAt).getTime(),
      new Date(customerSignature.createdAt).getTime()
    )
  );
  return addDays(lastSignatureDate, 10);
}
function areAllDocumentsApproved(timeline, documentIds) {
  for (const documentId of documentIds) {
    const status = deriveDocumentStatus(timeline, documentId);
    if (status !== "APPROVED") {
      return false;
    }
  }
  return true;
}
function getContractSignatureData(timeline, source) {
  const signatureEvent = timeline.find(
    (e) => e.type === "CONTRACT_SIGNED" && e.source === source
  );
  if (!signatureEvent) {
    return null;
  }
  return signatureEvent.context;
}
function getContractSignatureWithDate(timeline, source) {
  const signatureEvent = timeline.find(
    (e) => e.type === "CONTRACT_SIGNED" && e.source === source
  );
  if (!signatureEvent) {
    return null;
  }
  const context = signatureEvent.context;
  return {
    ...context,
    signedAt: new Date(signatureEvent.createdAt)
  };
}
function getAnyDocumentRejectionReason(timeline, currentDocumentIds) {
  const documentEvents = timeline.filter(
    (e) => e.type === "DOCUMENT_SUBMITTED" || e.type === "DOCUMENT_APPROVED" || e.type === "DOCUMENT_REJECTED"
  ).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  const latestByDocId = /* @__PURE__ */ new Map();
  for (const event of documentEvents) {
    const context = event.context;
    if (context?.documentId && !latestByDocId.has(context.documentId)) {
      latestByDocId.set(context.documentId, event);
    }
  }
  for (const [documentId, event] of latestByDocId.entries()) {
    if (currentDocumentIds && !currentDocumentIds.includes(documentId)) {
      continue;
    }
    if (event.type === "DOCUMENT_REJECTED") {
      const context = event.context;
      return context.reason ?? null;
    }
  }
  return null;
}
function hasAnyDocumentRejected(timeline, currentDocumentIds) {
  return getAnyDocumentRejectionReason(timeline, currentDocumentIds) !== null;
}
function deriveVendorTTStatus(timeline) {
  const ttEvents = timeline.filter(
    (e) => e.type === "VENDOR_TT_UPLOADED" || e.type === "VENDOR_TT_APPROVED" || e.type === "VENDOR_TT_REJECTED"
  ).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  if (ttEvents.length === 0) {
    return "NOT_UPLOADED";
  }
  const latestEvent = ttEvents[0];
  switch (latestEvent.type) {
    case "VENDOR_TT_UPLOADED":
      return "PENDING";
    case "VENDOR_TT_APPROVED":
      return "APPROVED";
    case "VENDOR_TT_REJECTED":
      return "REJECTED";
    default:
      return "NOT_UPLOADED";
  }
}
function getVendorTTRejectionReason(timeline) {
  const ttEvents = timeline.filter(
    (e) => e.type === "VENDOR_TT_UPLOADED" || e.type === "VENDOR_TT_APPROVED" || e.type === "VENDOR_TT_REJECTED"
  ).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  if (ttEvents.length === 0) {
    return null;
  }
  const latestEvent = ttEvents[0];
  if (latestEvent.type === "VENDOR_TT_REJECTED") {
    const context = latestEvent.context;
    return context.reason ?? null;
  }
  return null;
}
function getVendorTTUploadedAt(timeline) {
  const uploadEvent = timeline.filter((e) => e.type === "VENDOR_TT_UPLOADED").sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )[0];
  if (!uploadEvent) {
    return null;
  }
  return new Date(uploadEvent.createdAt);
}
function areBothTTsApproved(timeline) {
  const caTTApproved = timeline.some((e) => e.type === "TT_APPROVED");
  const vendorTTApproved = timeline.some(
    (e) => e.type === "VENDOR_TT_APPROVED"
  );
  return caTTApproved && vendorTTApproved;
}

// packages/isomorphic/src/lib/retry-until.ts
import pRetry from "p-retry";
async function retryUntil(fn, predicate, options = {}) {
  const { onFailedAttempt, ...pRetryOptions } = options;
  return pRetry(
    async (attempt) => {
      const result = await fn();
      if (await predicate(result)) {
        return result;
      }
      throw new Error("Predicate not satisfied");
    },
    {
      ...pRetryOptions,
      onFailedAttempt: async (error) => {
        if (onFailedAttempt) {
          await onFailedAttempt(error, error.attemptNumber, error.retriesLeft);
        }
      }
    }
  );
}

// packages/isomorphic/src/lib/vendor-exposure.ts
function exposeVendorInfo(vendor, timeline) {
  const { bothSigned } = deriveContractSignatureStatus(timeline);
  if (!bothSigned) {
    return { exposed: false, id: vendor.id };
  }
  return {
    exposed: true,
    id: vendor.id,
    name: vendor.name,
    email: vendor.email,
    contact: vendor.contact
  };
}

// packages/isomorphic/src/lib/service-map.ts
var SERVICE_MAP = {
  standard: "\u0645\u0648\u0639\u062F \u0645\u0639 \u0637\u0628\u064A\u0628",
  second_opinion: "\u0631\u0623\u064A \u0637\u0628\u064A \u062B\u0627\u0646\u064D"
};
var GENDER_MAP = {
  male: "\u0630\u0643\u0631",
  female: "\u0623\u0646\u062B\u0649"
};

// packages/isomorphic/src/lib/time-slots.ts
import { addHours, getHours, getMinutes, setMinutes } from "date-fns";
function roundDown30(date) {
  const minutes = getMinutes(date);
  const roundedMinutes = minutes < 30 ? 0 : 30;
  return setMinutes(date, roundedMinutes);
}
function roundUp30(date) {
  const minutes = getMinutes(date);
  if (minutes === 0 || minutes === 30) return date;
  const roundedMinutes = minutes < 30 ? 30 : 0;
  return roundedMinutes === 0 ? setMinutes(addHours(date, 1), 0) : setMinutes(date, 30);
}
function roundTo30(date) {
  const minutes = getMinutes(date);
  if (minutes < 15) {
    return setMinutes(date, 0);
  } else if (minutes < 45) {
    return setMinutes(date, 30);
  } else {
    return setMinutes(addHours(date, 1), 0);
  }
}
function overlap(slots) {
  const intervals = slots.map(({ startTime: start, endTime: end }) => ({
    start: Math.min(start, end),
    end: Math.max(start, end)
  }));
  for (let i = 1; i < intervals.length; i++) {
    for (let j = 0; j < i; j++) {
      if (intervals[i].start < intervals[j].end && intervals[i].end > intervals[j].start) {
        return i;
      }
    }
  }
  return -1;
}
function dateToMinutes(date) {
  return getHours(date) * 60 + getMinutes(date);
}
var APPOINTMENT_DURATION_MINUTES = 30;
var MINUTES_IN_DAY = 1440;
var LAST_MINUTE_OF_DAY = 1439;
function generateSlots(startTime, endTime) {
  const slots = [];
  for (let time = startTime; time < endTime; time += APPOINTMENT_DURATION_MINUTES) {
    slots.push({
      startTime: time,
      endTime: time + APPOINTMENT_DURATION_MINUTES
    });
  }
  return slots;
}

// packages/isomorphic/src/lib/validators.ts
import { parseISO } from "date-fns";
import parsePhoneNumber from "libphonenumber-js";
import { z } from "zod";
var validatePhoneNumber = async (value) => {
  const phoneNumber = parsePhoneNumber(value);
  return phoneNumber && phoneNumber.isValid();
};
var phoneValidator = (options) => async (value, ctx) => {
  try {
    const isEmpty = !value || value.length === 0;
    if (isEmpty && !options.validateEmpty) {
      return true;
    }
    const isValid = await validatePhoneNumber(value);
    if (!isValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "\u0631\u0642\u0645 \u0627\u0644\u062C\u0648\u0627\u0644 \u063A\u064A\u0631 \u0635\u062D\u064A\u062D"
      });
      return false;
    }
    return true;
  } catch (error) {
    console.error(error);
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "\u0631\u0642\u0645 \u0627\u0644\u062C\u0648\u0627\u0644 \u063A\u064A\u0631 \u0635\u062D\u064A\u062D"
    });
    return false;
  }
};
var futureDateSchema = z.string().datetime().transform((date) => parseISO(date)).refine(
  (date) => {
    const nowUTC = /* @__PURE__ */ new Date();
    return date >= nowUTC;
  },
  {
    message: "Date must not be in the past"
  }
);
var stringBoolean = z.coerce.string().or(z.coerce.boolean()).nullish().transform((value) => {
  if (value === null || value === void 0) {
    return false;
  }
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }
  return false;
});
var pageNumberSchema = z.coerce.number().int().positive().nullish().default(1).transform((value) => value === null || value === void 0 ? 1 : value);
var pageSizeSchema = z.coerce.number().int().min(1).max(100).nullish().default(50).transform((value) => value === null || value === void 0 ? 50 : value);
var searchQuerySchema = z.string().min(2).max(100).trim().optional();
var sortOrderSchema = z.enum(["asc", "desc"]).optional().default("asc");

// packages/isomorphic/src/lib/weekday.ts
var AR_DAYS_OF_WEEK = [
  "\u0627\u0644\u0633\u0628\u062A",
  "\u0627\u0644\u0623\u062D\u062F",
  "\u0627\u0644\u0627\u062B\u0646\u064A\u0646",
  "\u0627\u0644\u062B\u0644\u0627\u062B\u0627\u0621",
  "\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621",
  "\u0627\u0644\u062E\u0645\u064A\u0633",
  "\u0627\u0644\u062C\u0645\u0639\u0629"
];
var EN_DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
];
var EN_AR_DAYS_OF_WEEK = {
  SUNDAY: "\u0627\u0644\u0623\u062D\u062F",
  MONDAY: "\u0627\u0644\u0627\u062B\u0646\u064A\u0646",
  TUESDAY: "\u0627\u0644\u062B\u0644\u0627\u062B\u0627\u0621",
  WEDNESDAY: "\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621",
  THURSDAY: "\u0627\u0644\u062E\u0645\u064A\u0633",
  FRIDAY: "\u0627\u0644\u062C\u0645\u0639\u0629",
  SATURDAY: "\u0627\u0644\u0633\u0628\u062A"
};
var AR_TO_EN_DAYS_OF_WEEK = {
  \u0627\u0644\u0633\u0628\u062A: "SATURDAY",
  \u0627\u0644\u0623\u062D\u062F: "SUNDAY",
  \u0627\u0644\u0627\u062B\u0646\u064A\u0646: "MONDAY",
  \u0627\u0644\u062B\u0644\u0627\u062B\u0627\u0621: "TUESDAY",
  \u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621: "WEDNESDAY",
  \u0627\u0644\u062E\u0645\u064A\u0633: "THURSDAY",
  \u0627\u0644\u062C\u0645\u0639\u0629: "FRIDAY"
};
var weekdayMap = {
  0: "SUNDAY",
  1: "MONDAY",
  2: "TUESDAY",
  3: "WEDNESDAY",
  4: "THURSDAY",
  5: "FRIDAY",
  6: "SATURDAY"
};

// packages/isomorphic/src/lib/ordinal.ts
import { toOrdinal } from "n2words/ar-SA";
function toArabicOrdinal(n, gender) {
  return toOrdinal(n, { gender });
}

// packages/isomorphic/src/lib/profile-completeness.ts
var isBlank = (value) => value === null || value === void 0 || value.trim() === "";
var hasMissingSignatory = (fields) => isBlank(fields.signatoryFirstName) || isBlank(fields.signatoryLastName) || isBlank(fields.signatoryJobTitle) || isBlank(fields.signatoryEmail);
function getIncompleteProfileTab(user) {
  if (!user) return null;
  if (user.documentsComplete === false) return "account_documents";
  if (hasMissingSignatory(user)) return "signatory_info";
  return null;
}

// packages/isomorphic/src/lib/upload-mimes.ts
var ACCEPTED_UPLOAD_MIMES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp"
];
var TEN_MB = 10 * 1024 ** 2;
function isAcceptedUploadMime(mime) {
  return ACCEPTED_UPLOAD_MIMES.includes(mime);
}
function maxBytesFor(_mime) {
  return TEN_MB;
}
var UPLOAD_ACCEPT = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"]
};
var UPLOAD_CHUNK_BYTES = 8 * 256 * 1024;

// packages/isomorphic/src/index.ts
function isNullOrUndefined(value) {
  return value === void 0 || value === null;
}
function notNullOrUndefined(value) {
  return !isNullOrUndefined(value);
}
var formatCurrency = (amount) => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "SAR"
}).format(amount);
export {
  ACCEPTED_UPLOAD_MIMES,
  APPOINTMENT_DURATION_MINUTES,
  AR_DAYS_OF_WEEK,
  AR_TO_EN_DAYS_OF_WEEK,
  EN_AR_DAYS_OF_WEEK,
  EN_DAYS_OF_WEEK,
  GENDER_MAP,
  LAST_MINUTE_OF_DAY,
  MINUTES_IN_DAY,
  MIN_FIRST_PAYMENT_PERCENT,
  MUST_CHANGE_PASSWORD_CLAIM,
  SERVICE_MAP,
  SHORT_TERM_MAX_DAYS,
  SHORT_TERM_SPLIT_OPTIONS,
  UPLOAD_ACCEPT,
  UPLOAD_CHUNK_BYTES,
  areAllDocumentsApproved,
  areBothTTsApproved,
  calculateDailyWorkingHours,
  calculateMonthlyInstallments,
  calculateTotal,
  dateToMinutes,
  deriveContractSignatureStatus,
  deriveDocumentStatus,
  deriveTTDeadline,
  deriveTTStatus,
  deriveVendorTTStatus,
  exposeVendorInfo,
  formatCurrency,
  futureDateSchema,
  generateSlots,
  getAnyDocumentRejectionReason,
  getContractSignatureData,
  getContractSignatureWithDate,
  getDocumentRejectionReason,
  getIncompleteProfileTab,
  getTTRejectionReason,
  getTTUploadedAt,
  getVendorTTRejectionReason,
  getVendorTTUploadedAt,
  hasAnyDocumentRejected,
  isAcceptedUploadMime,
  isNullOrUndefined,
  isShortTermProject,
  maxBytesFor,
  notNullOrUndefined,
  overlap,
  pageNumberSchema,
  pageSizeSchema,
  parseScheduleTime,
  phoneValidator,
  retryUntil,
  roundDown30,
  roundTo30,
  roundUp30,
  searchQuerySchema,
  sortOrderSchema,
  stringBoolean,
  toArabicOrdinal,
  toScheduleTime,
  validatePhoneNumber,
  weekdayMap
};
//# sourceMappingURL=index.js.map
